import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Chat surface (T-0053 / E03-F04). Two endpoints:
 *
 *   - POST `/api/v1/engagements/:engagement_id/chat/messages` — ask a
 *     question; synchronous (≤5s p95 NFR per the API spec).
 *   - GET `/api/v1/engagements/:engagement_id/chat/sessions/:session_id` —
 *     fetch the transcript.
 *
 * Dual-auth: if the user is logged in as a firm-team caller, the Bearer
 * token wins. If only a viewer grant is present (post-magic-link redeem),
 * the API client switches to the `X-Viewer-Grant-Id` header via the
 * `asViewer: true` flag.
 */

export const ChatCitationSchema = z.object({
  citation_id: z.string().uuid(),
  document_id: z.string().uuid(),
  document_title: z.string(),
  page_number: z.number().int().nullable(),
  section_path: z.string().nullable(),
  /**
   * Optional per-citation confidence (0..1). The base T-0053 response
   * shape does not include this today — the `citations` table has no
   * confidence column. The schema accepts it so a future backend
   * widening lands without a client patch; the chat UI uses
   * `deriveCitationConfidence(...)` as the fallback.
   */
  confidence: z.number().min(0).max(1).optional().nullable(),
});
export type ChatCitation = z.infer<typeof ChatCitationSchema>;

export const ChatMessageResultSchema = z.object({
  session_id: z.string().uuid(),
  message_id: z.string().uuid(),
  answer: z.string(),
  query_path: z.string(),
  no_evidence: z.boolean(),
  citations: z.array(ChatCitationSchema),
  /**
   * Optional per-message confidence (0..1) used by `<AiConfidenceChip>`
   * above the AI bubble. Not yet returned by T-0053; the UI falls back
   * to `deriveMessageConfidence(...)` over the citation set.
   */
  confidence: z.number().min(0).max(1).optional().nullable(),
  /**
   * Optional per-message token cost in cents (BudgetGate ledger sum for
   * the turn). Persisted on `chat_messages.token_cost_cents` but not
   * surfaced on the route today; the schema accepts it so a future
   * widening lands without a client patch.
   */
  token_cost_cents: z.number().nonnegative().optional().nullable(),
});
export type ChatMessageResult = z.infer<typeof ChatMessageResultSchema>;

export const ChatTranscriptMessageSchema = z.object({
  message_id: z.string().uuid(),
  role: z.union([z.literal('user'), z.literal('assistant')]),
  content: z.string(),
  query_path: z.string().nullable(),
  no_evidence: z.boolean().nullable(),
  citations: z.array(ChatCitationSchema),
  created_at: z.string(),
  confidence: z.number().min(0).max(1).optional().nullable(),
  token_cost_cents: z.number().nonnegative().optional().nullable(),
});
export type ChatTranscriptMessage = z.infer<typeof ChatTranscriptMessageSchema>;

export const ChatTranscriptSchema = z.object({
  session_id: z.string().uuid(),
  messages: z.array(ChatTranscriptMessageSchema),
});
export type ChatTranscript = z.infer<typeof ChatTranscriptSchema>;

export interface PostChatMessageInput {
  question: string;
  /** When omitted, a new session is created server-side. */
  session_id?: string | null;
}

export function postChatMessage(
  engagementId: string,
  input: PostChatMessageInput,
  opts: { asViewer?: boolean } = {},
): Promise<ChatMessageResult> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/chat/messages`,
    ChatMessageResultSchema,
    {
      method: 'POST',
      body: input,
      ...(opts.asViewer ? { asViewer: true } : {}),
    },
  );
}

export function getChatTranscript(
  engagementId: string,
  sessionId: string,
  opts: { asViewer?: boolean } = {},
): Promise<ChatTranscript> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/chat/sessions/${encodeURIComponent(
      sessionId,
    )}`,
    ChatTranscriptSchema,
    opts.asViewer ? { asViewer: true } : {},
  );
}

export function useChatTranscriptQuery(
  engagementId: string | undefined,
  sessionId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['chat', 'transcript', engagementId, sessionId] as const,
    queryFn: () => getChatTranscript(engagementId!, sessionId!),
    enabled: Boolean(engagementId && sessionId),
  });
}

export function usePostChatMessageMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PostChatMessageInput) => postChatMessage(engagementId!, input),
    onSuccess: (result) => {
      if (engagementId) {
        void qc.invalidateQueries({
          queryKey: ['chat', 'transcript', engagementId, result.session_id],
        });
      }
    },
  });
}

// ─── UI-side derivations ─────────────────────────────────────────────────

/**
 * Heuristic per-citation confidence (0..1) for the chip. The base
 * T-0053 schema does not persist per-citation confidence — citations
 * are projection rows from the synthesizer's resolved set. Until the
 * backend widens, the chip needs *some* number to render in its
 * mono-confidence segment.
 *
 * Rules (v1):
 *   - If the API returned a `confidence` field on the citation, use it.
 *   - Otherwise return a conservative default per the design tokens
 *     mid-confidence band (the visual band still maps to ai-confidence-medium
 *     via aiConfidenceBand). 0.78 is the picked value because the
 *     prototype's representative chat citations sit in the 0.78–0.94
 *     range and 0.78 lands inside the medium band (0.65–0.84).
 *
 * TODO(T-0054): widen the API response to include real per-citation
 * confidence — the synthesizer already has scores from `RetrievalSet`
 * but drops them at the API boundary today.
 */
export function deriveCitationConfidence(cite: ChatCitation): number {
  if (typeof cite.confidence === 'number') return cite.confidence;
  return 0.78;
}

/**
 * Heuristic per-message confidence for `<AiConfidenceChip>`. The base
 * T-0053 schema does not persist per-message confidence either.
 *
 * Rules (v1):
 *   - If the API returned `confidence` on the message, use it.
 *   - Else if `no_evidence`, surface 0 — the user must see "no
 *     evidence" framed as low-confidence even though the value itself
 *     is synthesized.
 *   - Else: mean of citation confidences (using the per-citation
 *     fallback). Floors at 0.65 so the chip stays in the medium band
 *     when citations are present (a grounded answer with citations
 *     always reads as ≥ medium per the prototype).
 *
 * TODO(T-0054): widen the API response. The synthesizer can compute
 * this from the retrieval set top-K scores.
 */
export function deriveMessageConfidence(message: {
  confidence?: number | null;
  no_evidence?: boolean | null;
  citations: readonly ChatCitation[];
}): number {
  if (typeof message.confidence === 'number') return message.confidence;
  if (message.no_evidence) return 0;
  if (message.citations.length === 0) return 0.6;
  const sum = message.citations.reduce((acc, c) => acc + deriveCitationConfidence(c), 0);
  const mean = sum / message.citations.length;
  return Math.max(0.65, mean);
}
